import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  hostname: string;
  isLive: boolean;
  /** Called on successful 204; parent invalidates queries and closes any open wizard panel. */
  onDeleted?: () => void;
}

/**
 * Delete a custom-domain row. Mid-wizard rows take a simple confirm; live
 * rows require the user to type the exact hostname to enable Delete and
 * surface a 30-second propagation notice.
 */
export function DeleteDialog({
  open,
  onOpenChange,
  orgSlug,
  projectSlug,
  rowId,
  hostname,
  isLive,
  onDeleted,
}: Props) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState("");

  const del = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.delete(
        `/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}`,
        isLive ? { confirm: hostname } : undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] });
      setConfirmText("");
      onDeleted?.();
      onOpenChange(false);
    },
  });

  const liveMatch = confirmText === hostname;
  const disabled = del.isPending || (isLive && !liveMatch);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setConfirmText("");
        onOpenChange(o);
      }}
    >
      <DialogContent>
        {isLive ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-danger flex-shrink-0" />
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Remove live hostname</h3>
                <p className="text-xs text-muted-foreground">
                  Removing <span className="font-mono">{hostname}</span> stops serving traffic at
                  that hostname. Already-issued certs may continue serving for up to ~30 seconds
                  due to edge caches.
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="delete-confirm" className="text-xs">
                Type <span className="font-mono">{hostname}</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={hostname}
                className="font-mono text-sm"
                autoFocus
              />
            </div>
            {del.error && <div className="text-xs text-danger">{del.error.message}</div>}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                className="bg-danger text-danger-foreground hover:bg-danger/90"
                disabled={disabled}
                onClick={() => del.mutate()}
              >
                {del.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Remove hostname</h3>
              <p className="text-xs text-muted-foreground">
                Remove <span className="font-mono">{hostname}</span>? It's still mid-wizard, so
                nothing is currently serving traffic.
              </p>
            </div>
            {del.error && <div className="text-xs text-danger">{del.error.message}</div>}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={disabled}
                onClick={() => del.mutate()}
              >
                {del.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Remove
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
